// Pure normalisation helpers for hike highlights (a.k.a. hike POIs).
//
// Highlights reach the detail dialog from three different places with three
// slightly different shapes:
//   * the hike map / GraphQL payload  -> `images`, `mediaAttachments`
//   * the hike planner store          -> `images`, `media`
//   * unsaved edits in the planner    -> `imagePreviewUrls`, `mediaSignedIds`
// Everything below flattens those into one shape so the dialog only has to know
// about a single representation, and so the mapping is unit-testable.

export interface HighlightPhoto {
  key: string;
  thumbUrl: string;
  fullUrl: string;
}

export interface HighlightAudioTrack {
  key: string;
  url: string;
  title: string | null;
}

export interface LatLng {
  lat: number;
  lng: number;
}

const isLive = (item: any): boolean => !!item && !item._destroy;

// Photos for the strip, in "saved first, pending uploads last" order. Each entry
// carries both the grid thumbnail and the full-size URL for the lightbox.
export function highlightPhotos(highlight: any): HighlightPhoto[] {
  if (!highlight) return [];

  const saved: HighlightPhoto[] = (highlight.images || [])
    .filter(isLive)
    .map((image: any, index: number) => {
      const file = image.file || {};
      const thumbUrl = file.mdUrl || file.lgUrl || file.url || image.url || '';
      const fullUrl = file.lgUrl || file.mdUrl || file.url || image.url || '';
      return {
        key: String(image.id ?? image.clientId ?? `image-${index}`),
        thumbUrl,
        fullUrl,
      };
    })
    .filter((photo: HighlightPhoto) => photo.thumbUrl.length > 0);

  const pending: HighlightPhoto[] = (highlight.imagePreviewUrls || [])
    .filter((url: any) => typeof url === 'string' && url.length > 0)
    .map((url: string, index: number) => ({
      key: `preview-${index}`,
      thumbUrl: url,
      fullUrl: url,
    }));

  return [...saved, ...pending];
}

// Playable audio tracks. `mediaAttachments`/`media` are the persisted shapes and
// `mediaSignedIds` the not-yet-saved uploads; entries without a playable URL and
// non-audio attachments (video, documents) are dropped. De-duplicated by URL so
// a payload carrying both persisted shapes doesn't render the same clip twice.
export function highlightAudioTracks(highlight: any): HighlightAudioTrack[] {
  if (!highlight) return [];

  const fromPersisted = (items: any[]): HighlightAudioTrack[] => items
    .filter(isLive)
    .filter((item: any) => !item.mediaType || item.mediaType === 'audio')
    .filter((item: any) => !!item.url)
    .map((item: any, index: number) => ({
      key: String(item.id ?? item.clientId ?? `audio-${index}`),
      url: item.url,
      title: item.title ?? null,
    }));

  const pending: HighlightAudioTrack[] = (highlight.mediaSignedIds || [])
    .filter(isLive)
    .filter((item: any) => !item.mediaType || item.mediaType === 'audio')
    .filter((item: any) => !!item.previewUrl)
    .map((item: any, index: number) => ({
      key: String(item.clientId ?? `pending-audio-${index}`),
      url: item.previewUrl,
      title: item.title ?? null,
    }));

  const tracks = [
    ...fromPersisted(highlight.mediaAttachments || []),
    ...fromPersisted(highlight.media || []),
    ...pending,
  ];

  const seen = new Set<string>();
  return tracks.filter((track) => {
    if (seen.has(track.url)) return false;
    seen.add(track.url);
    return true;
  });
}

// Highlights carry GeoJSON `[lng, lat]` coordinates. Returns null rather than
// NaNs when they're missing or malformed so callers can hide location actions.
export function highlightLatLng(highlight: any): LatLng | null {
  const coordinates = highlight?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const lng = Number(coordinates[0]);
  const lat = Number(coordinates[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;

  return { lat, lng };
}

// Shareable deep link back to this highlight on the map it was opened from.
// Only persisted highlights get one — an unsaved planner POI has no server id
// for the receiving page to look up. Any pre-existing `waypoint` selection is
// stripped so the recipient doesn't land on somebody else's waypoint.
export function highlightShareUrl(highlight: any, currentUrl: string): string | null {
  const id = highlight?.id;
  if (id === null || id === undefined || id === '') return null;

  try {
    const url = new URL(currentUrl);
    url.searchParams.delete('waypoint');
    url.searchParams.set('highlight', String(id));
    return url.toString();
  } catch (e) {
    return null;
  }
}

// The counterpart read: which highlight (if any) a URL asks the map to open.
export function highlightIdFromUrl(currentUrl: string): string | null {
  try {
    return new URL(currentUrl).searchParams.get('highlight');
  } catch (e) {
    return null;
  }
}

// Close enough to read the surroundings, but not so close that a shared
// highlight arrives with no context around it.
export const SHARED_HIGHLIGHT_MIN_ZOOM = 13;

// --- Opening a shared highlight ---------------------------------------------
//
// The three deterministic parts of honouring a `?highlight=<id>` deep link,
// kept out of the map component so they can be tested without a Mapbox
// instance: which highlight the URL names, how far to zoom, and what payload
// the dialog is handed. The component keeps only the side effects — the
// once-per-page-load flag, the flyTo and the dialog call.

// Ids arrive as strings from the URL and as numbers from GraphQL, so the match
// is on the string form of both. Returns null when the URL names no highlight,
// or names one this map hasn't loaded (a link to another hike, or to a
// highlight since deleted).
export function sharedHighlightFromUrl(currentUrl: string, highlights: any): any | null {
  const sharedId = highlightIdFromUrl(currentUrl);
  if (!sharedId) return null;
  if (!Array.isArray(highlights)) return null;

  return highlights.find((candidate: any) => String(candidate?.id) === sharedId) || null;
}

// Never zooms out: someone already closer in keeps their view.
export function sharedHighlightZoom(currentZoom: any): number {
  const zoom = Number(currentZoom);
  return Number.isFinite(zoom) ? Math.max(zoom, SHARED_HIGHLIGHT_MIN_ZOOM) : SHARED_HIGHLIGHT_MIN_ZOOM;
}

// The shape window.showHighlightDetail expects. The collections default to empty
// arrays so the dialog never has to guard them.
export function sharedHighlightDetail(highlight: any): any {
  return {
    id: highlight.id,
    clientId: highlight.clientId || highlight.id,
    type: highlight.type,
    title: highlight.title,
    description: highlight.description,
    dayNumber: highlight.dayNumber,
    coordinates: highlight.coordinates,
    images: highlight.images || [],
    mediaAttachments: highlight.mediaAttachments || [],
  };
}

export interface HighlightStageDestination {
  itemType: 'highlight';
  itemId: string;
  name: string;
  poiType: string;
  coordinates: [number, number];
}

// The planner's stage-destination payload for a highlight, used when it becomes
// a day's stage end or the trail start. Null when the highlight has no location.
export function highlightStageDestination(highlight: any, fallbackName: string): HighlightStageDestination | null {
  const latLng = highlightLatLng(highlight);
  if (!latLng) return null;

  return {
    itemType: 'highlight',
    itemId: String(highlight.clientId),
    name: highlight.title || fallbackName,
    poiType: highlight.type || 'other',
    coordinates: [latLng.lng, latLng.lat],
  };
}
